<template>
    <div class="admin-config-page">
        <div class="nk-block-head nk-block-head-sm">
            <div class="nk-block-between">
                <div class="nk-block-head-content">
                    <h3 class="nk-block-title page-title">{{ trans('configuration.server_configuration') }}</h3>
                    <div class="nk-block-des text-soft">
                        <p>Choose the provisioning backend and maintain saved hosting credentials.</p>
                    </div>
                </div>
            </div>
        </div>
        <div class="admin-config-layout admin-linear-page">
            <configuration-sidebar menu="server"></configuration-sidebar>
            <div class="admin-config-panel">
                <div class="admin-config-panel__inner">
                    <section class="admin-config-section" :aria-label="trans('configuration.server_configuration')">
                        <h4 class="admin-config-section__title">{{ trans('configuration.server_configuration') }}</h4>
                        <p class="admin-config-section__description">{{ trans('configuration.server_configuration_hint') }}</p>

                        <div v-if="configForm.server" class="alert alert-light border mb-4">
                            <div class="caption-text text-uppercase text-soft mb-2">{{ trans('configuration.server_saved_summary') }}</div>
                            <ul class="list list-plain mb-0 small">
                                <li><strong>{{ trans('configuration.choose_server') }}:</strong> {{ serverLabel }}</li>
                                <template v-if="configForm.server === 'cpanel'">
                                    <li><strong>cPanel Host:</strong> {{ configForm.cpanel_host || '—' }}</li>
                                    <li><strong>cPanel Port:</strong> {{ configForm.cpanel_port || '—' }}</li>
                                    <li><strong>cPanel Username:</strong> {{ configForm.cpanel_username || '—' }}</li>
                                    <li><strong>cPanel Password:</strong> {{ cpanel_has_password ? '•••••••• (saved)' : '—' }}</li>
                                    <li><strong>{{ trans('configuration.cpanel_api_token') }}:</strong> {{ cpanel_has_api_token ? '•••••••• (saved)' : '—' }}</li>
                                </template>
                                <template v-if="configForm.server === 'plesk'">
                                    <li><strong>Plesk Host:</strong> {{ configForm.plesk_host || '—' }}</li>
                                    <li><strong>Plesk Username:</strong> {{ configForm.plesk_username || '—' }}</li>
                                    <li><strong>Plesk Webspace Id:</strong> {{ configForm.plesk_webspace_id || '—' }}</li>
                                    <li><strong>Plesk Password:</strong> {{ plesk_has_password ? '•••••••• (saved)' : '—' }}</li>
                                </template>
                            </ul>
                        </div>

                        <form class="mt-3" @submit.prevent="submit" @keydown="configForm.errors.clear($event.target.name)">
                            <div class="form-group">
                                <label for="">{{ trans('configuration.choose_server') }}</label>
                                <select v-model="configForm.server" class="form-control" @change="configForm.errors.clear('server')" name="server">
                                    <option value="">{{ trans('general.select_one') }}</option>
                                    <option value="local">Local</option>
                                    <option value="cpanel">cPanel</option>
                                    <option value="plesk">Plesk</option>
                                </select>
                                <show-error :form-name="configForm" prop-name="server"></show-error>
                            </div>
                            <div class="row" v-if="configForm.server === 'cpanel'">
                                <div class="col-12 col-md-6 form-group">
                                    <label for="">cPanel Host</label>
                                    <input class="form-control" type="text" v-model="configForm.cpanel_host" name="cpanel_host" placeholder="e.g. server.hostname.com or shared IP">
                                    <show-error :form-name="configForm" prop-name="cpanel_host"></show-error>
                                </div>
                                <div class="col-12 col-md-6 form-group">
                                    <label for="">cPanel Port</label>
                                    <input class="form-control" type="text" v-model="configForm.cpanel_port" name="cpanel_port" placeholder="2083 (SSL) or 2082">
                                    <show-error :form-name="configForm" prop-name="cpanel_port"></show-error>
                                </div>
                                <div class="col-12 col-md-6 form-group">
                                    <label for="">cPanel Username</label>
                                    <input class="form-control" type="text" v-model="configForm.cpanel_username" name="cpanel_username" placeholder="cPanel Username">
                                    <show-error :form-name="configForm" prop-name="cpanel_username"></show-error>
                                </div>
                                <div class="col-12 col-md-6 form-group">
                                    <label for="">cPanel Password</label>
                                    <input class="form-control" type="password" autocomplete="new-password" v-model="configForm.cpanel_password" name="cpanel_password" placeholder="cPanel Password">
                                    <small v-if="cpanel_has_password" class="form-text text-muted">{{ trans('configuration.password_unchanged_hint') }}</small>
                                    <show-error :form-name="configForm" prop-name="cpanel_password"></show-error>
                                </div>
                                <div class="col-12 form-group">
                                    <label for="">{{ trans('configuration.cpanel_api_token') }}</label>
                                    <input class="form-control font-monospace" type="password" autocomplete="new-password" v-model="configForm.cpanel_api_token" name="cpanel_api_token" placeholder="Paste API token from cPanel → Security → Manage API Tokens">
                                    <small class="form-text text-muted">{{ trans('configuration.cpanel_api_token_hint') }}</small>
                                    <small v-if="cpanel_has_api_token" class="form-text text-muted d-block">{{ trans('configuration.password_unchanged_hint') }}</small>
                                    <show-error :form-name="configForm" prop-name="cpanel_api_token"></show-error>
                                </div>
                            </div>
                            <div class="row" v-if="configForm.server === 'plesk'">
                                <div class="col-12 col-md-6 form-group">
                                    <label for="">Plesk Host</label>
                                    <input class="form-control" type="text" v-model="configForm.plesk_host" name="plesk_host" placeholder="Plesk Host">
                                    <show-error :form-name="configForm" prop-name="plesk_host"></show-error>
                                </div>
                                <div class="col-12 col-md-6 form-group">
                                    <label for="">Plesk Username</label>
                                    <input class="form-control" type="text" v-model="configForm.plesk_username" name="plesk_username" placeholder="Plesk Username">
                                    <show-error :form-name="configForm" prop-name="plesk_username"></show-error>
                                </div>
                                <div class="col-12 col-md-6 form-group">
                                    <label for="">Plesk Password</label>
                                    <input class="form-control" type="password" autocomplete="new-password" v-model="configForm.plesk_password" name="plesk_password" placeholder="Plesk Password">
                                    <small v-if="plesk_has_password" class="form-text text-muted">{{ trans('configuration.password_unchanged_hint') }}</small>
                                    <show-error :form-name="configForm" prop-name="plesk_password"></show-error>
                                </div>
                                <div class="col-12 col-md-6 form-group">
                                    <label for="">Plesk Webspace Id</label>
                                    <input class="form-control" type="text" v-model="configForm.plesk_webspace_id" name="plesk_webspace_id" placeholder="Plesk Webspace Id">
                                    <show-error :form-name="configForm" prop-name="plesk_webspace_id"></show-error>
                                </div>
                            </div>
                            <div class="admin-config-actions">
                                <button type="submit" class="btn btn-sm admin-toolbar-btn admin-toolbar-btn--solid">{{ trans('general.save') }}</button>
                            </div>
                        </form>
                    </section>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import configurationSidebar from '../system-config-sidebar';

    export default {
        components: { configurationSidebar },
        data() {
            return {
                cpanel_has_password: false,
                cpanel_has_api_token: false,
                plesk_has_password: false,
                configForm: new Form({
                    config_type: '',
                    server: '',
                    cpanel_host: '',
                    cpanel_port: '',
                    cpanel_username: '',
                    cpanel_password: '',
                    cpanel_api_token: '',
                    plesk_host: '',
                    plesk_password: '',
                    plesk_username: '',
                    plesk_webspace_id: ''
                }, false)
            };
        },
        computed: {
            serverLabel() {
                const labels = { local: 'Local', cpanel: 'cPanel', plesk: 'Plesk' };

                return labels[this.configForm.server] || this.configForm.server || '—';
            }
        },
        mounted() {
            if (!helper.hasPermission('access-configuration')) {
                helper.notAccessibleMsg();
                this.$router.push('/home');
            }

            axios.get('/api/configuration/server-settings')
                .then(response => response.data)
                .then(data => {
                    this.cpanel_has_password = !!data.cpanel_has_password;
                    this.cpanel_has_api_token = !!data.cpanel_has_api_token;
                    this.plesk_has_password = !!data.plesk_has_password;
                    const payload = Object.assign({}, data);
                    delete payload.cpanel_has_password;
                    delete payload.cpanel_has_api_token;
                    delete payload.plesk_has_password;
                    this.configForm = helper.formAssign(this.configForm, payload);
                    if (this.configForm.server !== null && this.configForm.server !== undefined && this.configForm.server !== '') {
                        this.configForm.server = String(this.configForm.server);
                    }
                    if (this.configForm.cpanel_port !== null && this.configForm.cpanel_port !== undefined && this.configForm.cpanel_port !== '') {
                        this.configForm.cpanel_port = String(this.configForm.cpanel_port);
                    }
                    if (this.configForm.cpanel_host === 0 || this.configForm.cpanel_host === '0') {
                        this.configForm.cpanel_host = '';
                    }
                }).catch(error => {
                    helper.showDataErrorMsg(error);
                });
        },
        methods: {
            submit() {
                this.configForm.config_type = 'system';
                this.configForm.post('/api/configuration')
                    .then(response => {
                        this.$store.dispatch('setConfig', this.configForm);
                        toastr.success(response.message);
                        axios.get('/api/configuration/server-settings').then(res => {
                            if (!res || !res.data) {
                                return;
                            }
                            const data = res.data;
                            this.cpanel_has_password = !!data.cpanel_has_password;
                            this.cpanel_has_api_token = !!data.cpanel_has_api_token;
                            this.plesk_has_password = !!data.plesk_has_password;
                            const payload = Object.assign({}, data);
                            delete payload.cpanel_has_password;
                            delete payload.cpanel_has_api_token;
                            delete payload.plesk_has_password;
                            this.configForm = helper.formAssign(this.configForm, payload);
                            this.configForm.cpanel_password = '';
                            this.configForm.cpanel_api_token = '';
                            this.configForm.plesk_password = '';
                            if (this.configForm.server) {
                                this.configForm.server = String(this.configForm.server);
                            }
                            if (this.configForm.cpanel_port) {
                                this.configForm.cpanel_port = String(this.configForm.cpanel_port);
                            }
                        }).catch(() => { /* ignore refresh errors */ });
                    })
                    .catch(error => {
                        helper.showErrorMsg(error);
                    });
            }
        }
    };
</script>
